import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import CreateContest from './CreateContest';
import proxy from '../../api/axios';
import { formatDateTime } from '../../common/utils';
import {
  ACCENT,
  AdminCard,
  AdminEmpty,
  AdminLoading,
  AdminPage,
  AdminPageHeader,
  AdminStatGrid,
  AdminToolbar,
  MetaChip,
  AdminFormDialog,
  useAdminPageTheme,
} from './adminUi';

const getContestStatus = (contest, now = Date.now()) => {
  const start = new Date(contest.startTime).getTime();
  const end = new Date(contest.endTime).getTime();
  if (now < start) return 'upcoming';
  if (now <= end) return 'live';
  return 'completed';
};

const statusMeta = {
  upcoming: { label: 'Upcoming', color: '#2563eb' },
  live: { label: 'Live', color: '#1a7f37' },
  completed: { label: 'Completed', color: '#8b949e' },
};

const ManageContests = () => {
  const { border, muted, isDark } = useAdminPageTheme();
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  const fetchContests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await proxy.get('/contests/admin');
      setContests(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load contests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContests();
  }, [fetchContests]);

  const stats = useMemo(() => {
    const now = Date.now();
    let upcoming = 0;
    let live = 0;
    let completed = 0;
    contests.forEach((c) => {
      const s = getContestStatus(c, now);
      if (s === 'upcoming') upcoming += 1;
      else if (s === 'live') live += 1;
      else completed += 1;
    });
    return { total: contests.length, upcoming, live, completed };
  }, [contests]);

  const filteredContests = useMemo(() => {
    const now = Date.now();
    return [...contests]
      .filter((contest) => {
        if (statusFilter === 'all') return true;
        return getContestStatus(contest, now) === statusFilter;
      })
      .filter((contest) =>
        contest.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
  }, [contests, searchQuery, statusFilter]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await proxy.delete(`/contests/${deleteTarget._id}`);
      toast.success('Contest deleted');
      setDeleteTarget(null);
      fetchContests();
    } catch (error) {
      toast.error('Failed to delete contest');
      console.error(error);
    }
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedContest(null);
    fetchContests();
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      borderRadius: 2.5,
      border: `1px solid ${border}`,
    },
  };

  return (
    <AdminPage>
      <Helmet>
        <title>Manage Contests | Admin | CrafterHack</title>
      </Helmet>

      <AdminPageHeader
        title="Manage contests"
        subtitle="Create weekly contests, edit schedules, and review past events"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchContests} sx={{ border: `1px solid ${border}` }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setSelectedContest(null);
                setOpenDialog(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none' }}
            >
              Create contest
            </Button>
          </>
        }
      />

      <AdminStatGrid
        items={[
          { label: 'TOTAL', value: stats.total },
          { label: 'UPCOMING', value: stats.upcoming, color: '#2563eb' },
          { label: 'LIVE', value: stats.live, color: '#1a7f37' },
          { label: 'COMPLETED', value: stats.completed, color: '#8b949e' },
        ]}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {[
          { id: 'all', label: `All (${stats.total})` },
          { id: 'upcoming', label: `Upcoming (${stats.upcoming})` },
          { id: 'live', label: `Live (${stats.live})` },
          { id: 'completed', label: `Completed (${stats.completed})` },
        ].map((item) => (
          <Button
            key={item.id}
            size="small"
            variant={statusFilter === item.id ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter(item.id)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              bgcolor: statusFilter === item.id ? ACCENT : 'transparent',
              borderColor: statusFilter === item.id ? ACCENT : border,
              boxShadow: 'none',
            }}
          >
            {item.label}
          </Button>
        ))}
      </Box>

      <AdminToolbar>
        <TextField
          size="small"
          placeholder="Search contests…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
      </AdminToolbar>

      {loading ? (
        <AdminLoading />
      ) : filteredContests.length === 0 ? (
        <AdminEmpty
          title="No contests found"
          description="Create a new contest or adjust your filters."
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredContests.map((contest) => {
            const status = getContestStatus(contest);
            const meta = statusMeta[status];
            const start = formatDateTime(contest.startTime);
            const end = formatDateTime(contest.endTime);

            return (
              <AdminCard
                key={contest._id}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                    <MetaChip label={meta.label} color={meta.color} isDark={isDark} />
                    {contest.problems?.length > 0 && (
                      <MetaChip
                        label={`${contest.problems.length} problems`}
                        isDark={isDark}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <EmojiEventsOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.98rem' }}>
                      {contest.title}
                    </Typography>
                    {contest.number != null && (
                      <Chip
                        size="small"
                        label={`#${contest.number}`}
                        sx={{
                          height: 20,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem', mt: 0.35 }}>
                    {start.date} {start.time} → {end.date} {end.time}
                    {contest.duration ? ` · ${contest.duration} min` : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedContest(contest);
                        setOpenDialog(true);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(contest)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AdminCard>
            );
          })}
        </Box>
      )}

      <AdminFormDialog
        open={openDialog}
        onClose={closeDialog}
        title={selectedContest ? 'Edit contest' : 'Create contest'}
        subtitle={
          selectedContest
            ? `Update schedule and problems for ${selectedContest.title}`
            : 'Set up a new coding contest for the community'
        }
        icon={selectedContest ? EmojiEventsOutlinedIcon : AddCircleOutlineIcon}
        formId={CreateContest.formId}
        submitLabel={selectedContest ? 'Save changes' : 'Create contest'}
        loading={formSaving}
        maxWidth="md"
      >
        <CreateContest
          contest={selectedContest}
          onClose={closeDialog}
          onSavingChange={setFormSaving}
        />
      </AdminFormDialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        sx={dialogSx}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete contest?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget
              ? `“${deleteTarget.title}” will be removed permanently.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default ManageContests;
